/**
 * Data layer for `sftp-scheduled-export`. Thin typed fetch wrappers over /api/v1/sftp-scheduled-export.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type SftpAuthMode = "password" | "key";
export type SftpExportFormat = "csv" | "xlsx" | "json" | "pdf";
export type SftpRunStatus = "ok" | "error" | "running" | "pending";

export interface SftpScheduledExportRow {
	id: string;
	tenant_id: string;
	name: string;
	host: string;
	port: number;
	user: string;
	auth_mode: SftpAuthMode;
	remote_path: string;
	schedule_cron: string;
	timezone_iana: string;
	report: string;
	format: SftpExportFormat;
	enabled: boolean;
	last_run_at: number | null;
	last_status: SftpRunStatus | null;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSftpScheduledExportResponse {
	count: number;
	items: SftpScheduledExportRow[];
}

export interface CreateSftpScheduledExportBody {
	name: string;
	host: string;
	port?: number;
	user: string;
	auth_mode: SftpAuthMode;
	password?: string;
	private_key?: string;
	remote_path: string;
	schedule_cron: string;
	timezone_iana?: string;
	report: string;
	format: SftpExportFormat;
	enabled?: boolean;
}

export interface TestConnectionResult {
	ok: boolean;
	message?: string;
	error?: string;
}

export interface RunNowResult {
	run_id: string;
	status: SftpRunStatus;
}

const BASE = "/api/v1/sftp-scheduled-export";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSftpScheduledExportResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSftpScheduledExportResponse>(res);
}

export async function create(body: CreateSftpScheduledExportBody): Promise<SftpScheduledExportRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<SftpScheduledExportRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export async function testConnection(
	body: Pick<
		CreateSftpScheduledExportBody,
		"host" | "port" | "user" | "auth_mode" | "password" | "private_key" | "remote_path"
	>,
): Promise<TestConnectionResult> {
	const res = await fetch(`${BASE}/test`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<TestConnectionResult>(res);
}

export async function runNow(id: string): Promise<RunNowResult> {
	const res = await fetch(`${BASE}/run/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RunNowResult>(res);
}

export const sftpScheduledExportQueryKeys = {
	all: ["sftp-scheduled-export"] as const,
	list: () => [...sftpScheduledExportQueryKeys.all] as const,
	detail: (id: string) => [...sftpScheduledExportQueryKeys.all, id] as const,
};
